import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, CheckCircle2, ClipboardList, History } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const PERIODOS = ["Primer Periodo", "Segundo Periodo", "Tercer Periodo", "Cuarto Periodo"];

const fechaHora = (iso) => {
  if (!iso) return "por definir";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric",
                                     hour: "2-digit", minute: "2-digit" });
};

export default function CoordAlertas() {
  const [alertas, setAlertas] = useState(null);
  const [error, setError] = useState(false);
  const [periodo, setPeriodo] = useState("");
  const [atendidas, setAtendidas] = useState(false);
  const [plan, setPlan] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/academico/alertas-rendimiento", {
        params: { periodo: periodo || undefined, incluir_atendidas: atendidas },
      });
      setAlertas(data);
      setError(false);
    } catch (e) {
      setError(true);
      setAlertas([]);
    }
  }, [periodo, atendidas]);

  useEffect(() => { cargar(); }, [cargar]);

  const atender = async (a) => {
    try {
      await api.post(`/academico/alertas-rendimiento/${a.id}/atender`);
      toast.success(`Alerta de ${a.estudiante_nombre} marcada como atendida`);
      cargar();
    } catch (e) {
      toast.error("No se pudo marcar la alerta como atendida");
    }
  };

  const abrirPlan = (a) => setPlan({
    alerta: a,
    plan: a.plan?.plan || "",
    fecha_reunion: a.plan?.fecha_reunion || "",
    lugar: a.plan?.lugar || "",
    docente: a.plan?.docente || [...new Set(a.materias.map((m) => m.docente).filter(Boolean))].join(", "),
    resultado: a.plan?.resultado || "",
  });

  const guardarPlan = async () => {
    setGuardando(true);
    try {
      const { data } = await api.put(`/academico/alertas-rendimiento/${plan.alerta.id}/plan`, {
        plan: plan.plan, fecha_reunion: plan.fecha_reunion || null, lugar: plan.lugar || null,
        docente: plan.docente || null, resultado: plan.resultado || null,
      });
      toast.success(data.aviso_al_tutor
        ? "Plan guardado. Se convocó al tutor por correo y por el portal."
        : "Plan de refuerzo guardado");
      setPlan(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e, "No se pudo guardar el plan de refuerzo"));
    } finally {
      setGuardando(false);
    }
  };

  if (!alertas) return <Loading />;

  return (
    <div data-testid="coord-alertas">
      <PageTitle title="Alertas de bajo rendimiento"
                 subtitle="Estudiantes con dos o más materias por debajo de 70 puntos en el mismo periodo. El sistema avisa automáticamente a coordinación, administración y al tutor cuando se publican las notas." />

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Periodo</label>
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}
                  data-testid="filtro-periodo-alertas"
                  className="block rounded-md border border-border bg-background px-3 py-2 text-sm">
            <option value="">Todos los periodos</option>
            {PERIODOS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <Button variant={atendidas ? "default" : "outline"} className="rounded-full"
                data-testid="toggle-atendidas" onClick={() => setAtendidas(!atendidas)}>
          {atendidas ? "Viendo también las atendidas" : "Ver también las atendidas"}
        </Button>
        <span className="text-sm text-muted-foreground" data-testid="conteo-alertas">
          {alertas.length} alerta(s)
        </span>
      </div>

      {error ? (
        <Empty testId="error-alertas">
          No se pudieron cargar las alertas. Vuelva a intentarlo en unos segundos.
        </Empty>
      ) : alertas.length === 0 ? (
        <Empty testId="sin-alertas">
          No hay alertas de bajo rendimiento. Se generan al publicar calificaciones.
        </Empty>
      ) : (
        <div className="space-y-4" data-testid="lista-alertas">
          {alertas.map((a) => (
            <article key={a.id} data-testid={`alerta-${a.id}`}
                     className={`rounded-lg border bg-card p-5 sm:p-6 ${a.atendida
                       ? "border-border/60 opacity-70" : "border-destructive/40"}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {a.atendida
                      ? <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                      : <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />}
                    <h2 className="font-display text-base font-bold sm:text-lg">
                      {a.estudiante_nombre}
                    </h2>
                    <Badge variant="outline">{a.grado || "Sin grado"}</Badge>
                    <Badge variant={a.atendida ? "secondary" : "destructive"}>
                      {a.atendida ? "Atendida" : `${a.materias.length} materias bajas`}
                    </Badge>
                    {a.plan?.plan && !a.atendida && (
                      <Badge variant="secondary" data-testid={`badge-plan-${a.id}`}>
                        Con plan de refuerzo
                      </Badge>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Matrícula {a.matricula || "—"} · {a.periodo} · Promedio del periodo{" "}
                    <strong className="font-mono">{a.promedio_periodo}</strong>
                    {a.tutor_nombre ? ` · Tutor: ${a.tutor_nombre}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-full"
                          data-testid={`plan-${a.id}`} onClick={() => abrirPlan(a)}>
                    <ClipboardList className="mr-2 h-4 w-4" />
                    {a.plan?.plan ? "Editar plan" : "Plan de refuerzo"}
                  </Button>
                  {!a.atendida && (
                    <Button size="sm" variant="outline" className="rounded-full"
                            data-testid={`atender-${a.id}`} onClick={() => atender(a)}>
                      Marcar como atendida
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2" data-testid={`materias-${a.id}`}>
                {a.materias.map((m) => (
                  <span key={m.materia_id}
                        className="rounded-full border border-destructive/30 bg-destructive/5 px-3 py-1 text-xs">
                    {m.materia} · <strong className="font-mono">{m.nota}</strong>
                    {m.docente ? <span className="text-muted-foreground"> · {m.docente}</span> : null}
                  </span>
                ))}
              </div>

              {a.plan?.plan && (
                <div className="mt-4 rounded-md border border-border/60 bg-muted/30 p-4"
                     data-testid={`detalle-plan-${a.id}`}>
                  <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <CalendarClock className="h-3.5 w-3.5" />
                    Reunión: <strong className="font-mono">{fechaHora(a.plan.fecha_reunion)}</strong>
                    {a.plan.lugar ? ` · ${a.plan.lugar}` : ""}
                    {a.plan.docente ? ` · Docente: ${a.plan.docente}` : ""}
                  </p>
                  <p className="mt-2 whitespace-pre-line text-sm leading-relaxed">{a.plan.plan}</p>
                  {a.plan.resultado && (
                    <p className="mt-3 text-sm">
                      <span className="text-xs uppercase tracking-widest text-muted-foreground">
                        Resultado de la reunión
                      </span>
                      <br />{a.plan.resultado}
                    </p>
                  )}
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Escrito por {a.plan.responsable} · {fechaHora(a.plan.actualizado_en)}
                    {a.historial_plan?.length > 1 && (
                      <span className="ml-2 inline-flex items-center gap-1">
                        <History className="h-3 w-3" /> {a.historial_plan.length} versiones
                      </span>
                    )}
                  </p>
                </div>
              )}

              {a.atendida && a.atendida_por && (
                <p className="mt-3 text-xs text-muted-foreground">Atendida por {a.atendida_por}</p>
              )}
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!plan} onOpenChange={(o) => !o && setPlan(null)}>
        <DialogContent data-testid="modal-plan-refuerzo" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Plan de refuerzo</DialogTitle>
            <DialogDescription>
              {plan?.alerta?.estudiante_nombre} · {plan?.alerta?.grado} · {plan?.alerta?.periodo}.
              Al guardar, el tutor recibe la convocatoria por correo y por el portal.
            </DialogDescription>
          </DialogHeader>
          {plan && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Plan y acciones acordadas</Label>
                <Textarea rows={5} value={plan.plan} data-testid="plan-texto"
                          placeholder="Ej.: tutorías de matemática dos veces por semana, práctica diaria de lectura en casa y evaluación de seguimiento en dos semanas."
                          onChange={(e) => setPlan({ ...plan, plan: e.target.value })} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Fecha y hora de la reunión</Label>
                  <Input type="datetime-local" value={plan.fecha_reunion} data-testid="plan-fecha"
                         onChange={(e) => setPlan({ ...plan, fecha_reunion: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Lugar o modalidad</Label>
                  <Input value={plan.lugar} data-testid="plan-lugar"
                         placeholder="Oficina de coordinación / videollamada"
                         onChange={(e) => setPlan({ ...plan, lugar: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Docente de la materia afectada</Label>
                <Input value={plan.docente} data-testid="plan-docente"
                       onChange={(e) => setPlan({ ...plan, docente: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Resultado de la reunión (se llena después)</Label>
                <Textarea rows={3} value={plan.resultado} data-testid="plan-resultado"
                          placeholder="Acuerdos alcanzados, compromiso de la familia, próxima revisión."
                          onChange={(e) => setPlan({ ...plan, resultado: e.target.value })} />
              </div>
              <Button className="rounded-full py-6" data-testid="guardar-plan"
                      disabled={guardando || plan.plan.trim().length < 5} onClick={guardarPlan}>
                {guardando ? "Guardando…" : "Guardar y convocar al tutor"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
